import { useQuery, useMutation, queryCache } from 'react-query';
import { useToasts } from 'freemium-ui';
import request from 'helpers/request';

const jsonParseResponseError = mutationArr => {
  const [mutationFunc, mutationObj] = mutationArr;
  return mutationObj.error
    ? [
        mutationFunc,
        {
          ...mutationObj,
          error: JSON.parse(mutationObj.error.message)
        }
      ]
    : mutationArr;
};
export const useIncident = (id, fallbackAction) =>
  useQuery([{ incident: String(id) }], async () => {
    const response = await request(`/incidents/${String(id)}`);
    if (response.status === 404) {
      if (typeof fallbackAction === 'function') fallbackAction();
    } else return response;
  });

export const useIncidentUpdates = id =>
  useQuery([{ incidentUpdates: String(id) }], async () => {
    const response = await request(
      `/incident-updates/?incident=${String(id)}&ordering=display_time`
    );
    return response.results;
  });

export const useInterimStatus = () =>
  useQuery(['interimStatus'], async () => {
    const { results } = await request(`/incident-interim-status`);
    results.sort((a, b) => a.order - b.order);
    return results;
  });

export const useIncidentStatus = (incidentId, fallbackAction) =>
  useQuery([{ incidentStatus: String(incidentId) }], async () => {
    const response = await request(`/incident-statuses/${incidentId}`);
    if (response.status === 404) {
      if (typeof fallbackAction === 'function') fallbackAction();
    } else return response;
    return response;
  });

export const useIncidentSource = () =>
  useQuery('incidentSources', async () => {
    const { results } = await request('/incident-source');
    results.sort((a, b) => a.order - b.order);
    return results;
  });

export const useTwitterAccounts = () =>
  useQuery('twitterAccounts', async () => {
    const { results } = await request('/twitter-accounts/');
    return results;
  });

export const useIncidentEdit = isMaintenance => {
  const [, setToast] = useToasts();
  return useMutation(
    async ({ payload, id }) => {
      await request(`/incidents/${id}`, {
        method: 'PATCH',
        payload
      });
    },

    {
      onMutate: ({ payload, id }) => {
        queryCache.cancelQueries([{ incident: String(id) }]);
        const previousValue = queryCache.getQueryData([
          { incident: String(id) }
        ]);
        queryCache.setQueryData([{ incident: String(id) }], old => {
          if (payload.source) {
            old.source_mini = { id: payload.source };
          } else {
            return {
              ...old,
              ...payload
            };
          }
          return old;
        });
        return previousValue;
      },
      onSettled: (data, error) => {
        setToast({
          text: error
            ? `${isMaintenance ? 'Maintenance' : 'Incident'} update failed`
            : `${
                isMaintenance ? 'Maintenance' : 'Incident'
              } updated successfully`,
          type: error ? 'error' : 'success',
          delay: 3000
        });
      },
      onSuccess: (data, { id }) => {
        queryCache.invalidateQueries([{ incident: String(id) }]);
        queryCache.invalidateQueries([{ incidentStatus: String(id) }]);
      },
      onError: (err, { id }, previousValue) => {
        queryCache.setQueryData([{ incident: String(id) }], previousValue);
      }
    }
  );
};

export const useIncidentDelete = isMaintenance => {
  const [, setToast] = useToasts();
  return useMutation(
    async ({ id, deleteSync }) => {
      const url = `/incidents/${id}/${
        deleteSync
          ? '?set_affected_components_to_operational_in_incident_duration=true'
          : ''
      }`;
      await request(url, { method: 'DELETE' });
    },
    {
      onSettled: (data, error) => {
        setToast({
          text: error
            ? `${isMaintenance ? 'Maintenance' : 'Incident'} delete failed`
            : `${
                isMaintenance ? 'Maintenance' : 'Incident'
              } deleted successfully`,
          type: error ? 'error' : 'success',
          delay: 3000
        });
      },
      onSuccess: (data, { id }) => {
        queryCache.removeQueries([{ incident: String(id) }]);
        queryCache.removeQueries([{ incidentUpdates: String(id) }]);
        queryCache.removeQueries([{ incidentStatus: String(id) }]);
      }
    }
  );
};

export const useIncidentUpdatesPost = isMaintenance => {
  const [, setToast] = useToasts();
  const arr = useMutation(
    async payload => {
      const resp = await request(`/incident-updates/`, {
        method: 'POST',
        payload
      });
      if (resp.error) {
        throw new Error(JSON.stringify(resp.response));
      }
      return resp;
    },
    {
      onMutate: payload => {
        queryCache.cancelQueries([
          { incidentUpdates: String(payload.incident) }
        ]);
        const previousValue = queryCache.getQueryData([
          { incidentUpdates: String(payload.incident) }
        ]);
        queryCache.setQueryData(
          [{ incidentUpdates: String(payload.incident) }],
          old => old.concat(payload)
        );
        return previousValue;
      },
      onError: (err, { incident }, previousValue) => {
        queryCache.setQueryData(
          [{ incidentUpdates: String(incident) }],
          previousValue
        );
      },
      onSuccess: (data, { incident }) => {
        queryCache.invalidateQueries([{ incident: String(incident) }]);
        queryCache.invalidateQueries([{ incidentStatus: String(incident) }]);
        queryCache.invalidateQueries([{ incidentUpdates: String(incident) }]);
        queryCache.invalidateQueries(['components']);
      },
      onSettled: (data, error) => {
        setToast({
          text: error
            ? `${
                isMaintenance ? 'Maintenance' : 'Incident'
              } update create failed`
            : `${
                isMaintenance ? 'Maintenance' : 'Incident'
              } update posted successfully`,
          type: error ? 'error' : 'success',
          delay: 3000
        });
      }
    }
  );
  return jsonParseResponseError(arr);
};

export const useIncidentUpdatesEdit = isMaintenance => {
  const [, setToast] = useToasts();
  return useMutation(
    async ({ payload, id }) => {
      await request(`/incident-updates/${id}`, {
        method: 'PATCH',
        payload
      });
    },
    {
      onMutate: ({ payload, id, incidentId }) => {
        queryCache.cancelQueries([{ incidentUpdates: String(incidentId) }]);
        const previousValue = queryCache.getQueryData([
          { incidentUpdates: String(incidentId) }
        ]);
        queryCache.setQueryData(
          [{ incidentUpdates: String(incidentId) }],
          old =>
            old.map(eachUpdate => {
              if (eachUpdate.id === id) {
                return payload;
              }
              return eachUpdate;
            })
        );
        return previousValue;
      },
      onError: (err, { incidentId }, previousValue) =>
        queryCache.setQueryData(
          [{ incidentUpdates: String(incidentId) }],
          previousValue
        ),
      onSuccess: (data, { incidentId }) => {
        queryCache.invalidateQueries([{ incidentUpdates: String(incidentId) }]);
        queryCache.invalidateQueries([{ incident: String(incidentId) }]);
      },
      onSettled: (data, error) => {
        setToast({
          text: error
            ? `${isMaintenance ? 'Maintenance' : 'Incident'} update failed`
            : `${
                isMaintenance ? 'Maintenance' : 'Incident'
              } update updated successfully`,
          type: error ? 'error' : 'success',
          delay: 3000
        });
      }
    }
  );
};

export const useIncidentUpdatesDelete = isMaintenance => {
  const [, setToast] = useToasts();
  return useMutation(
    async ({ id }) => {
      await request(`/incident-updates/${id}`, {
        method: 'DELETE'
      });
    },
    {
      onMutate: ({ id, incidentId }) => {
        queryCache.cancelQueries([{ incidentUpdates: String(incidentId) }]);
        const previousValue = queryCache.getQueryData([
          { incidentUpdates: String(incidentId) }
        ]);
        queryCache.setQueryData(
          [{ incidentUpdates: String(incidentId) }],
          old => old.filter(eachUpdate => eachUpdate.id !== id)
        );
        return previousValue;
      },
      onError: (err, { incidentId }, previousValue) =>
        queryCache.setQueryData(
          [{ incidentUpdates: String(incidentId) }],
          previousValue
        ),
      onSettled: (data, error) => {
        setToast({
          text: error
            ? `${
                isMaintenance ? 'Maintenance' : 'Incident'
              } update delete failed`
            : `${
                isMaintenance ? 'Maintenance' : 'Incident'
              } update deleted successfully`,
          type: error ? 'error' : 'success',
          delay: 3000
        });
      }
    }
  );
};

export const useIncidentTemplatesSearch = () =>
  useMutation(async searchTerm => {
    const response = await request(`/incident-templates/?search=${searchTerm}`);
    return (response && response.results) || [];
  });

export const useCancelMaintenance = () => {
  const [allToasts, setToast] = useToasts();
  return useMutation(
    async ({ payload, id }) => {
      const url = `/incidents/${id}/cancel`;
      await request(url, { method: 'POST', payload });
    },
    {
      onSettled: (data, error) => {
        if (allToasts && allToasts.length) {
          allToasts[0].cancel();
        }
        setToast({
          text: error
            ? 'Maintenance cancel failed'
            : 'Maintenance Canceled successfully',
          type: error ? 'error' : 'success',
          delay: 3000
        });
      },
      onSuccess: (data, { id }) => {
        queryCache.removeQueries([{ incident: String(id) }]);
        queryCache.removeQueries([{ incidentUpdates: String(id) }]);
        queryCache.removeQueries([{ incidentStatus: String(id) }]);
      }
    }
  );
};

export const useIncidentSubscriberDelete = () => {
  const [, setToast] = useToasts();
  return useMutation(
    async ({ id }) => {
      const url = `/incident-subscribers/${id}`;
      await request(url, {
        method: 'DELETE'
      });
    },
    {
      onMutate: ({ key }) => {
        queryCache.removeQueries(key);
      },
      onSettled: (data, error) => {
        setToast({
          text: error
            ? `Incident follower delete failed, try again later!`
            : `Incident follower deleted successfully`,
          type: error ? 'error' : 'success',
          delay: 3000
        });
      }
    }
  );
};
